import { EyeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@pesto/features';
import { Button } from '@pesto/ui/components/Button';
import { cn } from '@pesto/utils';
import { Wrench } from 'lucide-react';
import { useRef, useState } from 'react';

import { useCurrentUserQuery, UserRole } from '../../__generated__/graphql/api';
import { IS_PROD } from '../../constants/global-env';
import { useIsPublicRoute } from '../../hooks/useIsPublicRoute';
import { useRoleMimickingStore } from '../../stores/useRoleMimickingStore';
import { DevMenuComponent } from './components/DevMenuComponent';

export const FloatingDevTools = () => {
  const [isHovered, setIsHovered] = useState(false);
  const { isMimicking } = useRoleMimickingStore();
  const { isAuthenticated } = useAuth();
  const isPublicRoute = useIsPublicRoute();
  const { data } = useCurrentUserQuery({ skip: isPublicRoute || !isAuthenticated() });
  const backendRole = data?.currentUser?.role;
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  if (IS_PROD) return null;

  if (backendRole !== UserRole.SuperAdmin) return null;

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  return (
    <>
      <div className="fixed bottom-6 left-3 z-50" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {isHovered && <DevMenuComponent />}

        <Button
          size="sm"
          className={cn(
            'bg-background h-12 w-12 rounded-lg shadow-lg transition-all duration-200',
            isMimicking && 'border-primary bg-primary/10 border-2',
            isHovered && 'scale-110',
          )}
          variant={isMimicking ? 'primary' : 'outline'}
        >
          {isMimicking ? <EyeIcon className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
        </Button>
      </div>
    </>
  );
};
